import React from 'react';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import type { AppointmentOriginEvent } from '../../services/appointments';
import { ORIGIN_EVENT_RENDERERS } from './originEventRenderers';

interface OdoContextCardProps {
    patientId: string;
    originEvent: AppointmentOriginEvent | null;
    isLoading?: boolean;
}

/**
 * "ODO note": surfaces the timeline event that prompted the appointment,
 * dispatched per event `type` via originEventRenderers/, so the practice
 * sees why ODO asked for the appointment before they book it. Display-only
 * - the Composer never prefills the Reason field from this; the practice
 * picks their own.
 *
 * An origin event of an unrecognized/unhandled `type` (or no origin event
 * at all) renders nothing (a safe default - new event types show up as "no
 * card" rather than breaking).
 */
const OdoContextCard: React.FC<OdoContextCardProps> = ({
    patientId,
    originEvent,
    isLoading = false
}) => {
    const renderer = originEvent ? ORIGIN_EVENT_RENDERERS[originEvent.type] : undefined;
    const hasEventContent = !!(renderer && originEvent && renderer.hasContent(originEvent.payload));

    if (!isLoading && !hasEventContent) return null;

    const EventComponent = renderer?.Component;

    return (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-3">
            <div className="flex items-center gap-1.5 mb-1.5">
                <ChatBubbleBottomCenterTextIcon className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                <span className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">
                    ODO note :
                </span>
            </div>
            {isLoading ? (
                <p className="text-xs text-amber-700 opacity-60">Loading…</p>
            ) : (
                hasEventContent &&
                EventComponent &&
                originEvent && (
                    <EventComponent patientId={patientId} payload={originEvent.payload} />
                )
            )}
        </div>
    );
};

export default OdoContextCard;
